import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { isContentGeoBlocked } from "../../constants/geoBlocked";
import { getKey } from "../../utils/helpers";
import "./VideoPlayer.scss";

const GEO_BLOCKED_IMAGE =
  "https://rtvcplay-v2.s3.amazonaws.com/s3fs-public/2021-02/forbidden.jpg";

const useIsomorphicLayoutEffect =
  typeof window !== "undefined" ? useLayoutEffect : useEffect;

/**
 * Unified Kaltura player component — works in React 16 (master) and Next.js (v3).
 *
 * Props:
 *   url          {string}   HLS stream URL (required)
 *   title        {string}   Content title — shown in Adobe Analytics
 *   assetId      {string}   CMS asset ID — used as Adobe mediaId
 *   poster       {string}   Thumbnail shown before playback. When not provided,
 *                           falls back to the content's og:image from the store
 *                           (state.content.currentContent.meta_tags.og_image_0),
 *                           since the video object has no thumbnail yet (see connect below).
 *   isLive       {boolean}  true for live channels, false for VOD (default)
 *   duration     {number}   Duration in seconds (VOD only)
 *   nextVideoSlug {string}  Slug to navigate to at end of playback
 *   introTime    {number}   Seconds — show "Saltar intro" while currentTime < introTime
 *   creditTime   {number}   Seconds before end — show "Saltar créditos" / next-video button
 *   targetId     {string}   DOM id for the player container (auto-generated if omitted)
 *   onNavigate   {function} Called with a slug when navigation is needed.
 *                           In Next.js pass router.push. Falls back to window.location.href.
 *
 * Geo-blocking is resolved from the redux store directly (see connect below):
 *   geoblocking  {Array}    state.content.currentContent.geoblocking — countries
 *                           where the current content is allowed ([{value:"co"}] | ["co"]).
 *                           Empty/null = available worldwide.
 *   country      {string}   state.country.country — visitor country (cloudfront header).
 */
const VideoPlayer = ({
  url,
  title = "",
  assetId = "",
  poster = null,
  isLive = false,
  duration = 0,
  nextVideoSlug = null,
  introTime = null,
  creditTime = null,
  targetId: externalTargetId,
  onNavigate,
  geoblocking = null,
  country = null,
  storePoster = null
}) => {
  const targetId = externalTargetId || `kp-${assetId || "player"}`;
  const playerRef = useRef(null);
  const isGeoBlocked = isContentGeoBlocked(geoblocking, country);
  const posterUrl = poster || storePoster || "";

  const [showSkipIntro, setShowSkipIntro] = useState(false);
  const [showSkipCredits, setShowSkipCredits] = useState(false);
  const [showNextVideo, setShowNextVideo] = useState(false);

  const navigate = (slug) => {
    if (onNavigate) {
      onNavigate(slug);
    } else {
      window.location.href = slug;
    }
  };

  useIsomorphicLayoutEffect(() => {
    if (typeof window === "undefined" || !window.KalturaPlayer || !url) return;
    if (isGeoBlocked) return;

    let player;
    try {
      player = window.KalturaPlayer.setup({
        targetId,
        provider: {},
        plugins: {
          adobeHeartbeat: {
            title,
            assetId,
            type: isLive ? "live" : "vod",
            duration
          }
        },
        playback: {
          options: {
            html5: {
              hls: { startLevel: 0 }
            }
          }
        }
      });
      const sources = {
        hls: [{ mimetype: "application/x-mpegurl", url }]
      };
      if (posterUrl) sources.poster = posterUrl;
      player.setMedia({ sources });
      playerRef.current = player;
      window.currentPlayer = player;
    } catch (e) {
      console.error("[VideoPlayer] setup error:", e);
      return;
    }

    const onTimeUpdate = () => {
      const ct = player.currentTime || 0;

      if (introTime != null) {
        setShowSkipIntro(ct < introTime);
      }

      if (!isLive && duration > 0 && creditTime != null) {
        const creditsStart = duration - creditTime;
        setShowSkipCredits(ct >= creditsStart);

        if (nextVideoSlug) {
          setShowNextVideo(ct >= creditsStart);
          if (ct >= duration - 2) {
            navigate(nextVideoSlug);
          }
        }
      }
    };

    player.addEventListener(player.Event.TIME_UPDATE, onTimeUpdate);

    return () => {
      try {
        if (window.currentPlayer === player) window.currentPlayer = null;
        player.destroy();
      } catch (_) {}
      playerRef.current = null;
    };
  }, [url, isGeoBlocked]);

  const handleSkipIntro = () => {
    if (playerRef.current && introTime != null) {
      playerRef.current.currentTime = introTime;
    }
    setShowSkipIntro(false);
  };

  const handleSkipCredits = () => {
    if (playerRef.current && duration > 0 && creditTime != null) {
      playerRef.current.currentTime = duration - 1;
    }
    setShowSkipCredits(false);
  };

  if (isGeoBlocked) {
    return (
      <div className="videoPlayerWrapper">
        <img
          className="videoPlayer__geoBlocked"
          src={GEO_BLOCKED_IMAGE}
          alt="Este contenido no está disponible en tu país"
        />
      </div>
    );
  }

  return (
    <div className="videoPlayerWrapper">
      <div id={targetId} className="kp-target" />
      {showSkipIntro && (
        <button className="videoPlayer__skipBtn videoPlayer__skipBtn--intro" onClick={handleSkipIntro}>
          Saltar intro
        </button>
      )}
      {showSkipCredits && !nextVideoSlug && (
        <button className="videoPlayer__skipBtn videoPlayer__skipBtn--credits" onClick={handleSkipCredits}>
          Saltar créditos
        </button>
      )}
      {showNextVideo && nextVideoSlug && (
        <div className="videoPlayer__nextVideo">
          <a
            href={nextVideoSlug}
            className="videoPlayer__nextVideoBtn"
            onClick={(e) => { e.preventDefault(); navigate(nextVideoSlug); }}
          >
            Siguiente Video
          </a>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = state => ({
  country: getKey(state, "country.country", null),
  geoblocking: getKey(state, "content.currentContent.geoblocking", null),
  // Fallback poster: the backend does not (yet) expose a thumbnail on the video
  // object, so we reuse the social share image (og:image) from meta_tags.
  // When `video.thumbnail.path` is added upstream, the explicit `poster` prop
  // passed from AtVideo will take precedence over this fallback.
  storePoster: getKey(
    state,
    ["content", "currentContent", "meta_tags", "og_image_0", "#attributes", "content"],
    null
  )
});

export default connect(mapStateToProps)(VideoPlayer);
